import React, { useState, useEffect } from 'react';
import { calendarAPI } from '../../lib/api';
import { cn, formatDate } from '../../lib/utils';
import { toast } from 'sonner';
import {
  Globe,
  Calculator,
  Calendar,
  AlertTriangle,
  Clock,
  ChevronRight,
  Loader2,
  Info,
  Plus,
  CheckCircle2,
  Shield,
  Scale
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { JurisdictionSelector } from '../common/JurisdictionSelector';

const RISK_COLORS = {
  critical: 'bg-red-100 text-red-700 border-red-200',
  high: 'bg-orange-100 text-orange-700 border-orange-200',
  medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  low: 'bg-green-100 text-green-700 border-green-200',
};

export const JurisdictionDeadlines = ({ caseId, onDeadlineCalculated, onCreateEvent }) => {
  const [selectedJurisdiction, setSelectedJurisdiction] = useState('');
  const [jurisdictionDetails, setJurisdictionDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  
  // Calculation form
  const [deadlineType, setDeadlineType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [calculationResult, setCalculationResult] = useState(null);

  useEffect(() => {
    if (selectedJurisdiction) {
      loadJurisdictionDetails();
    } else {
      setJurisdictionDetails(null);
    }
  }, [selectedJurisdiction]);

  const loadJurisdictionDetails = async () => {
    setLoading(true);
    try {
      const response = await calendarAPI.getJurisdictionDetails(selectedJurisdiction);
      setJurisdictionDetails(response.data);
      setDeadlineType('');
      setCalculationResult(null);
    } catch (err) {
      console.error('Failed to load jurisdiction details:', err);
      toast.error('Failed to load jurisdiction details');
    } finally {
      setLoading(false);
    }
  };

  const calculateDeadline = async () => {
    if (!selectedJurisdiction || !deadlineType || !startDate) {
      toast.error('Please select jurisdiction, deadline type, and start date');
      return;
    }

    setCalculating(true);
    try {
      const response = await calendarAPI.calculateDeadline(
        selectedJurisdiction,
        deadlineType,
        new Date(startDate).toISOString(),
        caseId
      );
      setCalculationResult(response.data);
      onDeadlineCalculated?.(response.data);
    } catch (err) {
      toast.error('Failed to calculate deadline');
    } finally {
      setCalculating(false);
    }
  };

  const handleCreateEventFromCalculation = () => {
    if (!calculationResult) return;
    
    onCreateEvent?.({
      title: `${deadlineType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} Deadline`,
      start_time: calculationResult.suggested_date,
      event_type: 'filing_deadline',
      priority: calculationResult.risk_level,
      description: `Auto-calculated based on ${calculationResult.jurisdiction} court rules. ${calculationResult.notes}`,
      non_movable: true,
    });
    
    toast.success('Deadline added to event form');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
          <Scale className="h-5 w-5 text-white" />
        </div>
        <div>
          <h3 className="font-semibold text-slate-900">Court Rules & Deadline Intelligence</h3>
          <p className="text-sm text-slate-500">Jurisdiction-aware deadline calculations</p>
        </div>
      </div>

      {/* Jurisdiction Selector - Now Searchable */}
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Select Jurisdiction</Label>
          <JurisdictionSelector
            value={selectedJurisdiction}
            onChange={setSelectedJurisdiction}
            placeholder="Search and select jurisdiction..."
          />
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        )}

        {/* Jurisdiction Details */}
        {jurisdictionDetails && !loading && (
          <Card className="bg-slate-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-slate-900">{jurisdictionDetails.name}</h4>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{jurisdictionDetails.country}</Badge>
                  {jurisdictionDetails.region_type && (
                    <Badge variant="secondary" className="text-xs">
                      {jurisdictionDetails.region_type}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Limitation Periods */}
              <div className="mb-4">
                <p className="text-sm font-medium text-slate-700 mb-2">Limitation Periods</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(jurisdictionDetails.limitation_periods || {}).map(([type, years]) => (
                    <Badge key={type} variant="outline" className="text-xs">
                      {type.replace(/_/g, ' ')}: {typeof years === 'number' ? `${years} yr${years > 1 ? 's' : ''}` : years}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Court Rules Accordion */}
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="rules" className="border-none">
                  <AccordionTrigger className="text-sm py-2">
                    <span className="flex items-center gap-2">
                      <Info className="h-4 w-4" />
                      View Court Rules ({Object.keys(jurisdictionDetails.court_rules || {}).length})
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-2 pt-2">
                      {Object.entries(jurisdictionDetails.court_rules || {}).map(([key, rule]) => (
                        <div key={key} className="p-2 bg-white rounded border text-sm">
                          <p className="font-medium text-slate-800">
                            {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </p>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {Object.entries(rule).map(([ruleKey, value]) => (
                              <span key={ruleKey} className="text-xs text-slate-500">
                                {ruleKey.replace(/_/g, ' ')}: {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>
        )}

        {/* Deadline Calculator */}
        {jurisdictionDetails && !loading && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Calculator className="h-4 w-4" />
                Calculate Deadline
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Deadline Type</Label>
                  <Select value={deadlineType} onValueChange={setDeadlineType}>
                    <SelectTrigger data-testid="deadline-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.keys(jurisdictionDetails.court_rules || {}).map((ruleKey) => (
                        <SelectItem key={ruleKey} value={ruleKey}>
                          {ruleKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Start Date (e.g., service date)</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    data-testid="start-date-input"
                  />
                </div>
              </div>

              <Button 
                onClick={calculateDeadline} 
                disabled={calculating || !deadlineType || !startDate}
                className="w-full"
                data-testid="calculate-deadline-btn"
              >
                {calculating ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Calculator className="h-4 w-4 mr-2" />
                )}
                Calculate Deadline
              </Button>

              {/* Calculation Result */}
              {calculationResult && (
                <Card className={cn("mt-4", RISK_COLORS[calculationResult.risk_level])}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Calendar className="h-4 w-4" />
                          <span className="font-semibold">
                            {formatDate(calculationResult.suggested_date)}
                          </span>
                        </div>
                        <p className="text-sm">
                          {calculationResult.days_count} days from start date
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline" className={RISK_COLORS[calculationResult.risk_level]}>
                            {calculationResult.risk_level.toUpperCase()} RISK
                          </Badge>
                          {calculationResult.extension_possible && (
                            <Badge variant="outline" className="text-xs">
                              Extension Possible
                            </Badge>
                          )}
                        </div>
                      </div>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={handleCreateEventFromCalculation}
                              data-testid="add-deadline-event-btn"
                            >
                              <Plus className="h-4 w-4 mr-1" />
                              Add to Calendar
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            Create calendar event with this deadline
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>

                    {calculationResult.is_assisted && (
                      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-current/20 text-xs">
                        <Shield className="h-3 w-3" />
                        <span>AI-Assisted calculation. {calculationResult.notes}</span>
                      </div>
                    )}

                    {/* Suggested Reminders */}
                    {calculationResult.suggested_reminders?.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-current/20">
                        <p className="text-xs font-medium mb-2">Suggested Reminders:</p>
                        <div className="flex flex-wrap gap-1">
                          {calculationResult.suggested_reminders.map((reminder, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {reminder.days_before === 0 ? 'Same day' : `${reminder.days_before} days before`}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default JurisdictionDeadlines;
